import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { AuthService } from '@/services/auth'
import { setJsonCookie } from '@/lib/cookies'
import { createSession } from '@/lib/session'
import { USER_ROLES } from '@/types/auth'
import OtpModal from '@/components/Security/OtpModal'

const loginSchema = z.object({
  email: z.string().min(1, 'Tài khoản là bắt buộc'),
  password: z
    .string()
    .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
    .min(1, 'Mật khẩu là bắt buộc'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [challengeToken, setChallengeToken] = useState<string>('')
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const navigate = useNavigate()

  const mutationLogin = useMutation({
    mutationFn: (
      data: z.infer<typeof loginSchema> & { rememberMe: boolean },
    ) => {
      return AuthService.login({
        usernameOrEmail: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
      })
    },
    onSuccess: (res) => {
      const data = res.data

      if (data.twoFactorRequired) {
        setChallengeToken(data.challengeToken!)
        setTwoFactorRequired(true)
        toast.success('Vui lòng xác thực 2 yếu tố để tiếp tục')
        return
      }

      // Check if user has required role for admin access
      if (
        data.user.role !== USER_ROLES.SUPER_ADMIN &&
        data.user.role !== USER_ROLES.ADMIN
      ) {
        toast.error('Bạn không có quyền truy cập vào trang quản trị')
        return
      }

      // Check if user account is active
      if (data.user.status !== 'active') {
        toast.error('Tài khoản của bạn đã bị vô hiệu hóa')
        return
      }

      const payload = {
        user: {
          id: data.user.id,
          userName: data.user.userName,
          email: data.user.email,
          role: data.user.role,
          status: data.user.status,
          isLinkedGoogle: data.user.isLinkedGoogle,
          twoFactorEnabled: data.user.twoFactorEnabled,
        },
        accessToken: data.token.accessToken,
        refreshToken: data.token.refreshToken,
        accessExpiresIn: data.token.accessExpiresIn,
        accessExpiresAt: data.token.accessExpiresAt,
        refreshExpiresIn: data.token.refreshExpiresIn,
        refreshExpiresAt: data.token.refreshExpiresAt,
      }

      // Show success message with role information
      const roleMessage =
        data.user.role === USER_ROLES.SUPER_ADMIN
          ? 'Đăng nhập thành công với quyền Super Admin'
          : 'Đăng nhập thành công với quyền Admin'

      toast.success(roleMessage)
      setJsonCookie(
        'authentication',
        payload,
        rememberMe ? { expires: 30 } : undefined,
      )
      createSession(payload)
      navigate({ to: '/' })
    },
    onError: (error: AxiosError<any>) => {
      toast.error(error.response?.data?.message || 'Đăng nhập thất bại')
    },
  })

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { handleSubmit } = form

  const onSubmit = (values: z.infer<typeof loginSchema>) => {
    mutationLogin.mutate({ ...values, rememberMe })
  }

  return (
    <div className="flex h-screen">
      {/* Left Column - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center bg-white px-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/appstorelogo.png"
                alt="Appstoreviet Logo"
                className="w-48 h-auto"
              />
            </div>
            <p className="text-gray-600 text-sm">
              Vui lòng nhập thông tin của bạn để bắt đầu.
            </p>
          </div>

          {/* Login Form */}
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6"
              noValidate={true}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Tên đăng nhập
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Nhập tài khoản của bạn"
                        className="h-12 border-gray-300 focus:border-primary focus:ring-primary"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Mật khẩu
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Nhập mật khẩu của bạn"
                          className="h-12 border-gray-300 focus:border-primary focus:ring-primary pr-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember"
                    type="checkbox"
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label
                    htmlFor="remember"
                    className="ml-2 text-sm text-gray-600"
                  >
                    Ghi nhớ đăng nhập
                  </label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary font-bold hover:underline"
                >
                  Quên mật khẩu?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg"
                disabled={mutationLogin.isPending}
              >
                {mutationLogin.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Đăng nhập
              </Button>
            </form>
          </Form>
        </div>
      </div>

      {/* Right Column - Dashboard Preview */}
      <div className="flex-1 bg-gradient-to-br from-primary to-primary/80 relative overflow-hidden flex items-center justify-center">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
              backgroundSize: '20px 20px',
            }}
          ></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center text-white px-8">
          {/* Dashboard Preview */}
          <div className="relative">
            <img
              src="/fakeDashboard.png"
              alt="Dashboard Preview"
              className="w-full max-w-md h-auto rounded-lg"
            />
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-4">Chào mừng trở lại!</h2>
            <p className="text-primary-foreground/80 text-md">
              Vui lòng nhập thông tin của bạn để bắt đầu.
            </p>
          </div>
        </div>
      </div>

      <OtpModal
        isOpen={twoFactorRequired}
        onClose={() => setTwoFactorRequired(false)}
        challengeToken={challengeToken}
      />
    </div>
  )
}
